import React, { useState, useEffect, useMemo } from 'react';
import useApi from '../hooks/useApi';
import { AlertTriangle, Clock, Building2, FileText, CheckCircle, ChevronLeft, ArrowRight, Search, Edit, Printer } from 'lucide-react';

const LaudosVencimentoPage = () => {
    const { apiFetch } = useApi();
    
    const [data, setData] = useState({ vencidos: [], a_vencer: [], renovados: [] });
    const [loading, setLoading] = useState(true);
    
    const [searchTerm, setSearchTerm] = useState('');
    
    const [view, setView] = useState('dashboard'); 
    const [activeCategory, setActiveCategory] = useState(null); 
    const [selectedCompany, setSelectedCompany] = useState(null);
    
    const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);
    const [newOsNumber, setNewOsNumber] = useState('');
    
    const [matchingReports, setMatchingReports] = useState([]);
    const [isLoadingMatches, setIsLoadingMatches] = useState(false);
    const [selectedNewReportId, setSelectedNewReportId] = useState('');

    // --- ESTADOS DO RELATÓRIO PDF ---
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [reportFilters, setReportFilters] = useState({
        vencidos: true,
        a_vencer: true,
        renovados: true,
        startDate: '',
        endDate: '',
        company: 'Todas'
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await apiFetch('/os-nsa-reports/expiring');
            setData(response || { vencidos: [], a_vencer: [], renovados: [] });
        } catch (error) {
            console.error("Erro ao buscar vencimentos:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const groupedData = useMemo(() => {
        if (!activeCategory) return {};
        const list = data[activeCategory] || [];
        return list.reduce((acc, curr) => {
            if (!acc[curr.cliente_nome]) acc[curr.cliente_nome] = [];
            acc[curr.cliente_nome].push(curr);
            return acc;
        }, {});
    }, [data, activeCategory]);

    const allReports = useMemo(() => {
        return [
            ...(data.vencidos || []).map(i => ({...i, statusCategory: 'vencidos'})),
            ...(data.a_vencer || []).map(i => ({...i, statusCategory: 'a_vencer'})),
            ...(data.renovados || []).map(i => ({...i, statusCategory: 'renovados'}))
        ];
    }, [data]);

    // Extrai lista única de empresas para o filtro do relatório
    const uniqueCompanies = useMemo(() => {
        const comps = new Set(allReports.map(r => r.cliente_nome));
        return Array.from(comps).sort();
    }, [allReports]);

    const filteredSearchReports = useMemo(() => {
        if (!searchTerm.trim()) return [];
        const term = searchTerm.toLowerCase();
        const termOnlyNumbers = term.replace(/[^\d]/g, ''); 

        return allReports.filter(r => {
            const cnpjOnlyNumbers = (r.cliente_cnpj || '').replace(/[^\d]/g, '');
            
            return (r.os_number && r.os_number.toLowerCase().includes(term)) ||
                   (r.os_renovacao && r.os_renovacao.toLowerCase().includes(term)) ||
                   (r.placa && r.placa.toLowerCase().includes(term)) ||
                   (r.chassi && r.chassi.toLowerCase().includes(term)) ||
                   (r.serie && r.serie.toLowerCase().includes(term)) ||
                   (r.cliente_nome && r.cliente_nome.toLowerCase().includes(term)) ||
                   (r.cliente_cnpj && r.cliente_cnpj.toLowerCase().includes(term)) ||
                   (termOnlyNumbers && cnpjOnlyNumbers.includes(termOnlyNumbers) && termOnlyNumbers.length > 3);
        });
    }, [allReports, searchTerm]);

    const handleCategoryClick = (category) => {
        setActiveCategory(category);
        setView('empresas');
    };

    const handleCompanyClick = (companyName) => {
        setSelectedCompany(companyName);
        setView('laudos');
    };

    const handleBack = () => {
        if (view === 'laudos') setView('empresas');
        else if (view === 'empresas') {
            setView('dashboard');
            setActiveCategory(null);
        }
    };

    const openRenewModal = async (report) => {
        setSelectedReport(report);
        setNewOsNumber(report.os_renovacao || '');
        setSelectedNewReportId(report.report_renovacao_id || '');
        setMatchingReports([]);
        setIsRenewModalOpen(true);
        setIsLoadingMatches(true);

        try {
            const params = new URLSearchParams({
                placa: report.placa,
                chassi: report.chassi,
                serie: report.serie,
                old_report_id: report.report_id
            });
            const matches = await apiFetch(`/os-nsa-reports/matching-renewals?${params.toString()}`);
            setMatchingReports(matches || []);
            
            if (report.report_renovacao_id) {
                setSelectedNewReportId(report.report_renovacao_id);
            } else if (matches && matches.length > 0) {
                setSelectedNewReportId(matches[0].report_id);
                setNewOsNumber(matches[0].os_number);
            }
        } catch (err) {
            console.error("Erro ao buscar laudos compatíveis:", err);
        } finally {
            setIsLoadingMatches(false);
        }
    };

    const submitRenewal = async () => {
        if (!selectedNewReportId) return alert("Por favor, selecione um laudo válido gerado para este equipamento na lista.");
        
        const selectedMatch = matchingReports.find(r => String(r.report_id) === String(selectedNewReportId));
        const osNumberToSave = selectedMatch ? selectedMatch.os_number : newOsNumber;

        try {
            await apiFetch(`/os-nsa-reports/${selectedReport.report_id}/renew`, {
                method: 'POST',
                body: { 
                    nova_os: osNumberToSave,
                    novo_report_id: selectedNewReportId 
                }
            });
            
            setIsRenewModalOpen(false);
            fetchData(); 
            
            if (view === 'laudos' && groupedData[selectedCompany]?.length === 1 && selectedReport.statusCategory !== 'renovados') {
                handleBack();
            }
        } catch (error) {
            alert("Erro ao processar renovação: " + error.message);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const parts = dateStr.split('-');
        if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
        return dateStr;
    };

    // --- FUNÇÃO DE GERAÇÃO DO RELATÓRIO PDF ---
    const handleGenerateReport = () => {
        // 1. Filtrar os dados com base nas escolhas do modal
        const filteredForReport = allReports.filter(r => {
            // Filtro de Status
            if (!reportFilters[r.statusCategory]) return false;
            
            // Filtro de Empresa
            if (reportFilters.company !== 'Todas' && r.cliente_nome !== reportFilters.company) return false;
            
            // Filtro de Data
            if (reportFilters.startDate && r.data_validade < reportFilters.startDate) return false;
            if (reportFilters.endDate && r.data_validade > reportFilters.endDate) return false;
            
            return true;
        });

        if (filteredForReport.length === 0) {
            alert("Nenhum laudo encontrado com os filtros selecionados.");
            return;
        }

        // 2. Ordenar por data de vencimento (Mais antigos primeiro)
        filteredForReport.sort((a, b) => new Date(a.data_validade) - new Date(b.data_validade));

        // 3. Montar a janela de impressão em HTML limpo e profissional
        const printWindow = window.open('', '_blank');
        
        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Relatório de Laudos</title>
                <style>
                    body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; padding: 20px; color: #333; }
                    h2 { text-align: center; color: #1e3a8a; margin-bottom: 5px; text-transform: uppercase; font-size: 20px;}
                    .meta { text-align: center; font-size: 12px; color: #666; margin-bottom: 25px; }
                    .filter-info { margin-bottom: 15px; font-size: 13px; background: #f8fafc; padding: 10px; border-radius: 5px; border: 1px solid #e2e8f0; }
                    table { width: 100%; border-collapse: collapse; font-size: 12px; }
                    th, td { border: 1px solid #cbd5e1; padding: 8px; text-align: left; }
                    th { background-color: #f1f5f9; font-weight: bold; color: #0f172a; }
                    tr:nth-child(even) { background-color: #f8fafc; }
                    .status-vencidos { color: #dc2626; font-weight: bold; }
                    .status-a_vencer { color: #d97706; font-weight: bold; }
                    .status-renovados { color: #16a34a; font-weight: bold; }
                    .summary { margin-top: 20px; font-weight: bold; text-align: right; font-size: 14px; }
                    @media print {
                        body { padding: 0; }
                        button { display: none; }
                    }
                </style>
            </head>
            <body>
                <h2>Relatório de Validade de Laudos Tensão Aplicada</h2>
                <div class="meta">Gerado em: ${new Date().toLocaleString('pt-BR')}</div>
                
                <div class="filter-info">
                    <strong>Filtros Aplicados:</strong><br/>
                    Empresa: ${reportFilters.company}<br/>
                    Período: ${reportFilters.startDate ? formatDate(reportFilters.startDate) : 'Sempre'} até ${reportFilters.endDate ? formatDate(reportFilters.endDate) : 'Sempre'}
                </div>

                <table>
                    <thead>
                        <tr>
                            <th>Empresa</th>
                            <th>OS Antiga</th>
                            <th>Vencimento</th>
                            <th>Placa</th>
                            <th>Chassi</th>
                            <th>Nº Série</th>
                            <th>Nova OS</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${filteredForReport.map(r => `
                            <tr>
                                <td>${r.cliente_nome}</td>
                                <td>${r.os_number}</td>
                                <td>${formatDate(r.data_validade)}</td>
                                <td>${r.placa}</td>
                                <td>${r.chassi}</td>
                                <td>${r.serie}</td>
                                <td>${r.os_renovacao || '-'}</td>
                                <td class="status-${r.statusCategory}">
                                    ${r.statusCategory === 'vencidos' ? 'VENCIDO' : r.statusCategory === 'a_vencer' ? 'A VENCER' : 'RENOVADO'}
                                </td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                
                <div class="summary">
                    Total de Equipamentos Listados: ${filteredForReport.length}
                </div>

                <script>
                    window.onload = function() { window.print(); }
                </script>
            </body>
            </html>
        `;

        printWindow.document.open();
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        setIsReportModalOpen(false); // Fecha o modal
    };

    const renderTableRows = (reportList, isSearchMode = false) => {
        if (!reportList || reportList.length === 0) return (
            <tr><td colSpan={isSearchMode ? "8" : (activeCategory === 'renovados' ? "7" : "6")} className="p-4 text-center text-gray-500">Nenhum laudo encontrado.</td></tr>
        );

        return reportList.map((report, idx) => {
            const cat = isSearchMode ? report.statusCategory : activeCategory;
            let statusColor = 'text-gray-700';
            if (cat === 'vencidos') statusColor = 'text-red-600';
            if (cat === 'a_vencer') statusColor = 'text-yellow-600';
            if (cat === 'renovados') statusColor = 'text-green-600';

            return (
                <tr key={idx} className="hover:bg-gray-50">
                    {isSearchMode && (
                        <td className="p-4 font-bold text-gray-800">{report.cliente_nome}</td>
                    )}
                    <td className="p-4 font-bold text-blue-600">
                        <div className="flex items-center gap-2"><FileText className="w-4 h-4"/> {report.os_number}</div>
                    </td>
                    <td className={`p-4 font-bold ${statusColor}`}>
                        {formatDate(report.data_validade)}
                    </td>
                    
                    {(isSearchMode || activeCategory === 'renovados') && (
                        <td className="p-4 font-bold text-gray-700">
                            {cat === 'renovados' ? (
                                <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs whitespace-nowrap">
                                    OS: {report.os_renovacao}
                                </span>
                            ) : (
                                <span className="text-gray-400">-</span>
                            )}
                        </td>
                    )}

                    <td className="p-4 text-gray-700">{report.placa}</td>
                    <td className="p-4 text-gray-700">{report.chassi}</td>
                    <td className="p-4 text-gray-700">{report.serie}</td>
                    <td className="p-4 text-center">
                        {cat === 'renovados' ? (
                            <button 
                                onClick={() => openRenewModal(report)}
                                className="px-3 py-1.5 bg-blue-600 text-white text-xs font-bold rounded shadow hover:bg-blue-700 flex items-center gap-2 mx-auto whitespace-nowrap"
                            >
                                <Edit className="w-4 h-4" /> Editar OS
                            </button>
                        ) : (
                            <button 
                                onClick={() => openRenewModal(report)}
                                className="px-3 py-1.5 bg-green-600 text-white text-xs font-bold rounded shadow hover:bg-green-700 flex items-center gap-2 mx-auto whitespace-nowrap"
                            >
                                <CheckCircle className="w-4 h-4" /> Renovado
                            </button>
                        )}
                    </td>
                </tr>
            );
        });
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* CABEÇALHO COM BOTÃO DE RELATÓRIO */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <h1 className="text-2xl font-bold text-gray-800">Controle de Validade de Laudos</h1>
                <button 
                    onClick={() => setIsReportModalOpen(true)}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white font-bold rounded-lg shadow-sm hover:bg-indigo-700 transition-colors"
                >
                    <Printer className="w-5 h-5" />
                    Gerar Relatório PDF
                </button>
            </div>

            {/* BARRA DE BUSCA GLOBAL */}
            <div className="relative mb-8">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                    type="text"
                    placeholder="Buscar por Empresa, CNPJ/CPF, OS (Antiga/Nova), Placa, Chassi ou Nº de Série..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none shadow-sm text-gray-700"
                />
            </div>

            {loading ? (
                <div className="text-center py-10 text-gray-500 animate-pulse font-semibold">Atualizando base de dados de laudos...</div>
            ) : (
                <>
                    {/* VISUALIZAÇÃO DE BUSCA */}
                    {searchTerm.trim() !== '' ? (
                        <div className="bg-white rounded-xl shadow-md border overflow-hidden">
                            <div className="bg-blue-600 p-4 text-white">
                                <h2 className="text-lg font-bold">Resultados da Busca</h2>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead className="bg-gray-50 border-b text-sm text-gray-600">
                                        <tr>
                                            <th className="p-4 font-semibold">Empresa</th>
                                            <th className="p-4 font-semibold">OS Antiga</th>
                                            <th className="p-4 font-semibold">Vencimento</th>
                                            <th className="p-4 font-semibold">Nova OS</th>
                                            <th className="p-4 font-semibold">Placa</th>
                                            <th className="p-4 font-semibold">Chassi</th>
                                            <th className="p-4 font-semibold">Nº Série</th>
                                            <th className="p-4 font-semibold text-center">Ação</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {renderTableRows(filteredSearchReports, true)}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        /* VISUALIZAÇÃO NORMAL (DASHBOARD) */
                        <>
                            {view === 'dashboard' && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {/* Card Vencidos */}
                                    <div 
                                        onClick={() => handleCategoryClick('vencidos')}
                                        className="bg-white rounded-xl shadow-md border-l-4 border-red-600 p-6 cursor-pointer hover:shadow-lg hover:-translate-y-1 transition-all flex items-center justify-between"
                                    >
                                        <div>
                                            <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1">Laudos Vencidos</p>
                                            <h2 className="text-4xl font-extrabold text-red-600">{data.vencidos?.length || 0}</h2>
                                        </div>
                                        <div className="p-4 bg-red-50 rounded-full">
                                            <AlertTriangle className="w-10 h-10 text-red-500" />
                                        </div>
                                    </div>

                                    {/* Card A Vencer */}
                                    <div 
                                        onClick={() => handleCategoryClick('a_vencer')}
                                        className="bg-white rounded-xl shadow-md border-l-4 border-yellow-500 p-6 cursor-pointer hover:shadow-lg hover:-translate-y-1 transition-all flex items-center justify-between"
                                    >
                                        <div>
                                            <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1">A Vencer (30 Dias)</p>
                                            <h2 className="text-4xl font-extrabold text-yellow-600">{data.a_vencer?.length || 0}</h2>
                                        </div>
                                        <div className="p-4 bg-yellow-50 rounded-full">
                                            <Clock className="w-10 h-10 text-yellow-500" />
                                        </div>
                                    </div>

                                    {/* NOVO: Card Renovados */}
                                    <div 
                                        onClick={() => handleCategoryClick('renovados')}
                                        className="bg-white rounded-xl shadow-md border-l-4 border-green-600 p-6 cursor-pointer hover:shadow-lg hover:-translate-y-1 transition-all flex items-center justify-between"
                                    >
                                        <div>
                                            <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1">Laudos Renovados</p>
                                            <h2 className="text-4xl font-extrabold text-green-600">{data.renovados?.length || 0}</h2>
                                        </div>
                                        <div className="p-4 bg-green-50 rounded-full">
                                            <CheckCircle className="w-10 h-10 text-green-600" />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* NÍVEL 2: LISTA DE EMPRESAS */}
                            {view === 'empresas' && (
                                <div className="bg-white rounded-xl shadow-md border overflow-hidden">
                                    <div className="bg-gray-100 p-4 border-b flex items-center gap-3">
                                        <button onClick={handleBack} className="p-2 bg-white rounded shadow-sm hover:bg-gray-50"><ChevronLeft className="w-5 h-5 text-gray-600"/></button>
                                        <h2 className="text-lg font-bold text-gray-800">
                                            Empresas - Categoria: {activeCategory === 'vencidos' ? 'Vencidos' : activeCategory === 'a_vencer' ? 'A Vencer' : 'Renovados'}
                                        </h2>
                                    </div>
                                    <div className="divide-y">
                                        {Object.keys(groupedData).length === 0 ? (
                                            <div className="p-8 text-center text-gray-500">Nenhuma empresa nesta categoria.</div>
                                        ) : (
                                            Object.keys(groupedData).sort().map((company, idx) => (
                                                <div 
                                                    key={idx} 
                                                    onClick={() => handleCompanyClick(company)}
                                                    className="p-4 flex items-center justify-between hover:bg-blue-50 cursor-pointer transition-colors"
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <div className="p-2 bg-blue-100 rounded-lg text-blue-600"><Building2 className="w-6 h-6" /></div>
                                                        <div>
                                                            <h3 className="font-bold text-gray-800 text-lg">{company}</h3>
                                                            <p className="text-sm text-gray-500">{groupedData[company].length} equipamento(s) listado(s)</p>
                                                        </div>
                                                    </div>
                                                    <ArrowRight className="w-5 h-5 text-gray-400" />
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* NÍVEL 3: LISTA DE LAUDOS (EQUIPAMENTOS) */}
                            {view === 'laudos' && (
                                <div className="bg-white rounded-xl shadow-md border overflow-hidden">
                                    <div className="bg-gray-100 p-4 border-b flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <button onClick={handleBack} className="p-2 bg-white rounded shadow-sm hover:bg-gray-50"><ChevronLeft className="w-5 h-5 text-gray-600"/></button>
                                            <h2 className="text-lg font-bold text-gray-800">{selectedCompany}</h2>
                                        </div>
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                                            activeCategory === 'vencidos' ? 'bg-red-100 text-red-700' : 
                                            activeCategory === 'renovados' ? 'bg-green-100 text-green-700' : 
                                            'bg-yellow-100 text-yellow-700'
                                        }`}>
                                            {activeCategory.replace('_', ' ')}
                                        </span>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <thead className="bg-gray-50 border-b text-sm text-gray-600">
                                                <tr>
                                                    <th className="p-4 font-semibold">OS Antiga</th>
                                                    <th className="p-4 font-semibold">
                                                        {activeCategory === 'renovados' ? 'Vencido Em' : 'Vencimento'}
                                                    </th>
                                                    {/* Título da nova coluna */}
                                                    {activeCategory === 'renovados' && (
                                                        <th className="p-4 font-semibold">Nova OS</th>
                                                    )}
                                                    <th className="p-4 font-semibold">Placa</th>
                                                    <th className="p-4 font-semibold">Chassi</th>
                                                    <th className="p-4 font-semibold">Nº Série</th>
                                                    <th className="p-4 font-semibold text-center">Ação</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y">
                                                {renderTableRows(groupedData[selectedCompany], false)}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            {/* MODAL DE FILTROS DO RELATÓRIO */}
            {isReportModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
                        <div className="bg-indigo-600 p-4 text-white font-bold flex justify-between items-center">
                            <span className="flex items-center gap-2"><Printer className="w-5 h-5"/> Gerar Relatório em PDF</span>
                        </div>
                        <div className="p-6">
                            <p className="text-gray-600 mb-6 text-sm">Selecione os filtros abaixo para montar o seu relatório de validade de laudos.</p>
                            
                            {/* Filtro de Status */}
                            <div className="mb-5">
                                <label className="block text-sm font-bold text-gray-700 mb-2">Status a incluir:</label>
                                <div className="flex flex-wrap gap-4">
                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                        <input 
                                            type="checkbox" 
                                            checked={reportFilters.vencidos} 
                                            onChange={(e) => setReportFilters({...reportFilters, vencidos: e.target.checked})}
                                            className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                                        /> 
                                        Vencidos
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                        <input 
                                            type="checkbox" 
                                            checked={reportFilters.a_vencer} 
                                            onChange={(e) => setReportFilters({...reportFilters, a_vencer: e.target.checked})}
                                            className="w-4 h-4 text-yellow-500 rounded focus:ring-yellow-500"
                                        /> 
                                        A Vencer
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                        <input 
                                            type="checkbox" 
                                            checked={reportFilters.renovados} 
                                            onChange={(e) => setReportFilters({...reportFilters, renovados: e.target.checked})}
                                            className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                                        /> 
                                        Renovados
                                    </label>
                                </div>
                            </div>

                            {/* Filtro de Empresa */}
                            <div className="mb-5">
                                <label className="block text-sm font-bold text-gray-700 mb-2">Empresa Cliente:</label>
                                <select 
                                    value={reportFilters.company}
                                    onChange={(e) => setReportFilters({...reportFilters, company: e.target.value})}
                                    className="w-full p-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                                >
                                    <option value="Todas">-- Todas as Empresas --</option>
                                    {uniqueCompanies.map((comp, idx) => (
                                        <option key={idx} value={comp}>{comp}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Filtro de Data */}
                            <div className="grid grid-cols-2 gap-4 mb-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Vencimento Inicial:</label>
                                    <input 
                                        type="date" 
                                        value={reportFilters.startDate}
                                        onChange={(e) => setReportFilters({...reportFilters, startDate: e.target.value})}
                                        className="w-full p-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-sm text-gray-700"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Vencimento Final:</label>
                                    <input 
                                        type="date" 
                                        value={reportFilters.endDate}
                                        onChange={(e) => setReportFilters({...reportFilters, endDate: e.target.value})}
                                        className="w-full p-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-sm text-gray-700"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                <button onClick={() => setIsReportModalOpen(false)} className="px-4 py-2 bg-gray-200 text-gray-700 font-bold rounded hover:bg-gray-300">
                                    Cancelar
                                </button>
                                <button 
                                    onClick={handleGenerateReport} 
                                    className="px-6 py-2 bg-indigo-600 text-white font-bold rounded hover:bg-indigo-700"
                                >
                                    Imprimir Relatório
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE RENOVAÇÃO COM SELEÇÃO INTELIGENTE */}
            {isRenewModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
                        <div className={`${selectedReport?.statusCategory === 'renovados' || activeCategory === 'renovados' ? 'bg-blue-600' : 'bg-green-600'} p-4 text-white font-bold flex justify-between items-center`}>
                            <span>{selectedReport?.os_renovacao ? 'Alterar OS de Renovação' : 'Confirmar Renovação'}</span>
                        </div>
                        <div className="p-6">
                            <p className="text-gray-600 mb-4 text-sm">
                                {selectedReport?.os_renovacao ? (
                                    <>Você está alterando a vinculação de renovação para o equipamento <b>{selectedReport?.placa !== 'N/A' && selectedReport?.placa !== '' ? selectedReport?.placa : selectedReport?.chassi}</b>.</>
                                ) : (
                                    <>Selecione o novo laudo (OS) gerado para o equipamento <b>{selectedReport?.placa !== 'N/A' && selectedReport?.placa !== '' ? selectedReport?.placa : selectedReport?.chassi}</b> para retirá-lo das pendências.</>
                                )}
                            </p>
                            
                            <div className="mb-6">
                                <label className="block text-sm font-bold text-gray-700 mb-2">Selecione a Nova OS</label>
                                
                                {isLoadingMatches ? (
                                    <div className="text-sm font-semibold text-blue-600 animate-pulse bg-blue-50 p-3 rounded">
                                        Buscando laudos recentes correspondentes...
                                    </div>
                                ) : matchingReports.length === 0 ? (
                                    <div className="text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200">
                                        Nenhum laudo novo encontrado para a Placa/Chassi/Série deste equipamento. <b>É necessário criar a OS e preencher o laudo novo antes de renová-lo aqui.</b>
                                    </div>
                                ) : (
                                    <select
                                        value={selectedNewReportId}
                                        onChange={(e) => {
                                            setSelectedNewReportId(e.target.value);
                                            const sel = matchingReports.find(r => String(r.report_id) === e.target.value);
                                            if (sel) setNewOsNumber(sel.os_number);
                                        }}
                                        className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                    >
                                        <option value="">-- Selecione o Novo Laudo --</option>
                                        {matchingReports.map(r => (
                                            <option key={r.report_id} value={r.report_id}>
                                                OS: {r.os_number} (Venc.: {formatDate(r.data_validade)})
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                            
                            <div className="flex justify-end gap-3">
                                <button onClick={() => setIsRenewModalOpen(false)} className="px-4 py-2 bg-gray-200 text-gray-700 font-bold rounded hover:bg-gray-300">
                                    Cancelar
                                </button>
                                <button 
                                    onClick={submitRenewal} 
                                    disabled={matchingReports.length === 0 || isLoadingMatches}
                                    className={`px-4 py-2 text-white font-bold rounded ${matchingReports.length === 0 || isLoadingMatches ? 'bg-gray-400 cursor-not-allowed' : (selectedReport?.os_renovacao ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700')}`}
                                >
                                    Salvar Alteração
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default LaudosVencimentoPage;